import React, { useState, useEffect, useRef, useCallback } from 'react'
import { getCards, getLocations } from '../utils/contentRepository'
import { normalizeString } from '../utils/helpers'
import { sanitizeHtml } from '../utils/html'

const isBingoCard = (item) =>
  item.status === 'released' && item.category !== 'Not Available'

const isBingoLocation = (item) => item.status === 'released'

const stripTags = (value = '') => String(value).replace(/<[^>]*>/g, '')

const stripCustomTags = (value = '') =>
  String(value)
    .replace(/<\/?(?!b\b|strong\b|i\b|em\b|br\b)[a-z][^>]*>/gi, '')
    .replace(/<>/g, '')

const BingoView = ({ language }) => {
  const [cards] = useState(() => getCards().filter(isBingoCard))
  const [locations] = useState(() => getLocations().filter(isBingoLocation))
  const [gameMode, setGameMode] = useState('cards')
  const [currentItem, setCurrentItem] = useState(null)
  const [score, setScore] = useState(0)
  const [totalAttempts, setTotalAttempts] = useState(0)
  const [gameStarted, setGameStarted] = useState(false)
  const [revealItem, setRevealItem] = useState(false)
  const [roundHasMiss, setRoundHasMiss] = useState(false)
  const [userAnswer, setUserAnswer] = useState('')
  const [feedback, setFeedback] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const gameBlockRef = useRef(null)
  const answerPanelRef = useRef(null)
  const usedKeysRef = useRef([])

  const t =
    language === 'RU'
      ? {
          heading: 'Угадайте карту или локацию по описанию',
          subtitle:
            'Проверьте, насколько хорошо вы знаете карты и локации Marvel Snap.',
          cardsMode: 'Карты',
          locationsMode: 'Локации',
          startGame: 'Начать игру',
          submit: 'Ответить',
          showAnswer: 'Показать ответ',
          nextCard: 'Следующая карта',
          reset: 'Сбросить игру',
          correct: 'Верно! Молодец!',
          incorrect: 'Неверно. Попробуйте еще раз!',
          guessed: 'Угадано',
          missed: 'Не угадано',
          totalCards: 'Всего карт',
          totalLocations: 'Всего локаций',
          guessCard: 'Угадайте карту по описанию:',
          guessLocation: 'Угадайте локацию по описанию:',
          noDescription: 'Описание недоступно',
          cardsIntroTitle: 'Карточная разминка',
          cardsIntroText:
            'Читайте эффект карты, вводите название и сверяйте ответ с оригинальной картой.',
          locationsIntroTitle: 'Разминка по локациям',
          locationsIntroText:
            'Локации в Marvel Snap — это области, куда выставляются карты. По описанию эффекта попробуйте угадать название локации.',
          placeholderCard: 'Введите название карты',
          placeholderLocation: 'Введите название локации',
        }
      : {
          heading: 'Guess the card or location by description',
          subtitle: 'Test how well you know Marvel Snap cards and locations.',
          cardsMode: 'Cards',
          locationsMode: 'Locations',
          startGame: 'Start game',
          submit: 'Submit',
          showAnswer: 'Show answer',
          nextCard: 'Next card',
          reset: 'Reset game',
          correct: 'Correct! Well done!',
          incorrect: 'Incorrect. Try again!',
          guessed: 'Guessed',
          missed: 'Missed',
          totalCards: 'Total cards',
          totalLocations: 'Total locations',
          guessCard: 'Guess the card by description:',
          guessLocation: 'Guess the location by description:',
          noDescription: 'No description available',
          cardsIntroTitle: 'Card warm-up',
          cardsIntroText:
            'Read the card effect, enter the name, and reveal the original card.',
          locationsIntroTitle: 'Location warm-up',
          locationsIntroText:
            'Locations are areas where cards are played. Read the effect and guess the location name.',
          placeholderCard: 'Enter card name',
          placeholderLocation: 'Enter location name',
        }

  const gameItems = gameMode === 'cards' ? cards : locations
  const promptLabel = gameMode === 'cards' ? t.guessCard : t.guessLocation
  const introTitle =
    gameMode === 'cards' ? t.cardsIntroTitle : t.locationsIntroTitle
  const introText = gameMode === 'cards' ? t.cardsIntroText : t.locationsIntroText
  const placeholder =
    gameMode === 'cards' ? t.placeholderCard : t.placeholderLocation
  const totalLabel = gameMode === 'cards' ? t.totalCards : t.totalLocations

  const selectRandomItem = useCallback(() => {
    if (!gameItems.length) return

    const available = gameItems.filter(
      (item) => !usedKeysRef.current.includes(`${gameMode}:${item.name}`),
    )
    const source = available.length > 0 ? available : gameItems
    const selected = source[Math.floor(Math.random() * source.length)]

    usedKeysRef.current =
      available.length === 0
        ? [`${gameMode}:${selected.name}`]
        : [...usedKeysRef.current, `${gameMode}:${selected.name}`]

    setCurrentItem(selected)
    setRevealItem(false)
    setRoundHasMiss(false)
    setUserAnswer('')
    setFeedback('')
    setShowSuggestions(false)
  }, [gameItems, gameMode])

  useEffect(() => {
    if (gameStarted && gameItems.length > 0) {
      selectRandomItem()
    }
  }, [gameStarted, gameItems.length, selectRandomItem])

  useEffect(() => {
    if (!gameStarted) return
    window.setTimeout(() => {
      gameBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }, [gameStarted])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        answerPanelRef.current &&
        !answerPanelRef.current.contains(event.target)
      ) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getAllNames = () => {
    const names = []
    gameItems.forEach((item) => {
      if (item.name) names.push(stripTags(item.name))
      if (item.nameEN) names.push(stripTags(item.nameEN))
    })
    return [...new Set(names)]
  }

  const handleInputChange = (value) => {
    setUserAnswer(value)
    if (value.length < 2) {
      setShowSuggestions(false)
      return
    }

    const filtered = getAllNames()
      .filter((name) => normalizeString(name).includes(normalizeString(value)))
      .slice(0, 8)

    setSuggestions(filtered)
    setShowSuggestions(filtered.length > 0)
  }

  const selectSuggestion = (suggestion) => {
    setUserAnswer(suggestion)
    setShowSuggestions(false)
  }

  const resetRoundState = () => {
    setRevealItem(false)
    setRoundHasMiss(false)
    setUserAnswer('')
    setFeedback('')
    setShowSuggestions(false)
  }

  const startGame = () => {
    usedKeysRef.current = []
    setScore(0)
    setTotalAttempts(0)
    setGameStarted(true)
    resetRoundState()
  }

  const resetGame = () => {
    usedKeysRef.current = []
    setScore(0)
    setTotalAttempts(0)
    setGameStarted(false)
    setCurrentItem(null)
    resetRoundState()
  }

  const changeGameMode = (mode) => {
    if (mode === gameMode) return
    usedKeysRef.current = []
    setGameMode(mode)
    setScore(0)
    setTotalAttempts(0)
    setGameStarted(false)
    setCurrentItem(null)
    resetRoundState()
  }

  const checkNameMatch = (normalizedUserAnswer, item) => {
    const name = normalizeString(stripTags(item.name))
    const nameEN = normalizeString(stripTags(item.nameEN))
    return normalizedUserAnswer === name || normalizedUserAnswer === nameEN
  }

  const checkAnswer = () => {
    if (!currentItem || !userAnswer.trim() || revealItem) return

    const normalizedAnswer = normalizeString(userAnswer.trim())
    const isCorrect = checkNameMatch(normalizedAnswer, currentItem)

    setTotalAttempts((prev) => prev + 1)
    if (isCorrect) {
      setScore((prev) => prev + 1)
      setFeedback(t.correct)
      setRevealItem(true)
    } else {
      setRoundHasMiss(true)
      setFeedback(t.incorrect)
    }
    setUserAnswer('')
    setShowSuggestions(false)
  }

  const showAnswer = () => {
    if (!currentItem || revealItem) return
    if (!roundHasMiss) {
      setTotalAttempts((prev) => prev + 1)
    }
    setFeedback('')
    setRevealItem(true)
    setUserAnswer('')
    setShowSuggestions(false)
  }

  const missedAnswers = totalAttempts - score

  const itemName = currentItem
    ? stripTags(language === 'RU' ? currentItem.name : currentItem.nameEN)
    : ''
  const itemDescription = currentItem
    ? stripCustomTags(
        (language === 'RU' ? currentItem.descr : currentItem.descrEN) ||
          t.noDescription,
      )
    : t.noDescription

  return (
    <div className="bingo-page">
      <div className="bingo-page-header">
        <h2>{t.heading}</h2>
        <p>{t.subtitle}</p>
      </div>

      <div className="bingo-view" ref={gameBlockRef}>
        <div className="game-stats">
          <div className="game-stat game-stat--correct">
            <span className="game-stat__label">{t.guessed}</span>
            <strong>{score}</strong>
          </div>
          <div className="game-stat game-stat--incorrect">
            <span className="game-stat__label">{t.missed}</span>
            <strong>{missedAnswers}</strong>
          </div>
          <div className="game-stat">
            <span className="game-stat__label">{totalLabel}</span>
            <strong>{gameItems.length}</strong>
          </div>
        </div>

        <div className="bingo-mode-switch" aria-label="Bingo mode">
          <button
            className={`tab ${gameMode === 'cards' ? 'active' : ''}`}
            onClick={() => changeGameMode('cards')}
            type="button"
          >
            {t.cardsMode}
          </button>
          <button
            className={`tab ${gameMode === 'locations' ? 'active' : ''}`}
            onClick={() => changeGameMode('locations')}
            type="button"
          >
            {t.locationsMode}
          </button>
        </div>

        {!gameStarted ? (
          <div className="bingo-setup">
            <div className="game-modes">
              <h3>{introTitle}</h3>
              <p>{introText}</p>
            </div>
            <button onClick={startGame} className="tab bingo-start" type="button">
              {t.startGame}
            </button>
          </div>
        ) : (
          <div className="bingo-game">
            {currentItem && (
              <div className="bingo-game-content">
                {revealItem ? (
                  <div className="bingo-card-display">
                    <div className="card">
                      <div className="card-image-wrapper">
                        <img
                          src={process.env.PUBLIC_URL + currentItem.imgUrl}
                          alt={itemName}
                          className="card-image"
                          loading="lazy"
                        />
                        {gameMode === 'cards' && (
                          <>
                            <span className="round-btn-cost">{currentItem.cost}</span>
                            <span className="round-btn-power">{currentItem.power}</span>
                          </>
                        )}
                      </div>
                      <h3
                        className="card-name"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(itemName) }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="bingo-mystery-card" aria-hidden="true">
                    <span>?</span>
                  </div>
                )}

                <div className="bingo-prompt">
                  <span className="bingo-prompt__label">{promptLabel}</span>
                  <div
                    className="bingo-description"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(itemDescription),
                    }}
                  />
                </div>

                <div className="bingo-answer-panel" ref={answerPanelRef}>
                  <form
                    className="bingo-answer-form"
                    onSubmit={(event) => {
                      event.preventDefault()
                      checkAnswer()
                    }}
                  >
                    <div className="bingo-input-wrapper">
                      <input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder={placeholder}
                        className="bingo-input"
                        autoComplete="off"
                      />
                      {feedback && (
                        <span
                          className={`bingo-inline-feedback ${
                            feedback === t.correct ? 'correct' : 'incorrect'
                          }`}
                        >
                          {feedback}
                        </span>
                      )}
                    </div>

                    <button type="submit" className="tab bingo-action">
                      {t.submit}
                    </button>
                    <button
                      type="button"
                      onClick={showAnswer}
                      className={`tab bingo-action ${
                        feedback === t.incorrect && !revealItem
                          ? 'bingo-action--hint'
                          : ''
                      }`}
                    >
                      {t.showAnswer}
                    </button>
                    <button
                      type="button"
                      onClick={selectRandomItem}
                      className={`tab bingo-action ${
                        feedback === t.correct || revealItem
                          ? 'bingo-action--hint'
                          : ''
                      }`}
                    >
                      {t.nextCard}
                    </button>
                    <button
                      type="button"
                      onClick={resetGame}
                      className="tab bingo-reset"
                    >
                      {t.reset}
                    </button>
                  </form>

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="autocomplete-suggestions">
                      {suggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          className="suggestion-item"
                          onClick={() => selectSuggestion(suggestion)}
                          type="button"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default BingoView
